import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PRODUCT } from "@/constants/testIds";
import { ZAR } from "@/lib/cart";
import { mediaUrl } from "@/lib/api";
import api from "@/lib/api";
import StarRating from "@/components/StarRating";
import { Eye, ArrowRight, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

const THIRTY_DAYS = 1000 * 60 * 60 * 24 * 30;

export default function ProductCard({ product, index = 0, onQuickView }) {
  const img = product.images?.[0];
  const onSale = product.compare_at && product.compare_at > product.price;
  const [rating, setRating] = useState(null);

  useEffect(() => {
    if (!window.__ve_rating_cache) {
      window.__ve_rating_cache = api.get("/reviews/summary/all").then((r) => r.data);
    }
    window.__ve_rating_cache.then((all) => setRating(all[product.slug]));
  }, [product.slug]);

  const lowStock = product.stock > 0 && product.stock <= 5;
  const isNew =
    product.created_at &&
    Date.now() - new Date(product.created_at).getTime() < THIRTY_DAYS;
  const endOfSale = onSale && lowStock;
  const isFeatured = product.featured;

  const salePct = onSale
    ? Math.round((1 - product.price / product.compare_at) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.04 }}
      data-testid={PRODUCT.card}
      className="group relative"
      style={{ perspective: "1200px" }}
    >
      <div
        className="relative w-full transition-transform duration-700 ease-out group-hover:[transform:rotateY(180deg)]"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* ---------- FRONT FACE ---------- */}
        <Link
          to={`/product/${product.slug}`}
          data-testid={PRODUCT.cardLink}
          className="block border border-white/10 bg-[#0d0d0d]"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
        >
          <div className="relative aspect-square overflow-hidden bg-gradient-to-b from-black to-[#141414]">
            {img ? (
              <img
                src={mediaUrl(img)}
                alt={product.name}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-white/20">no image</div>
            )}

            {/* Top-left compact badge stack */}
            <div className="absolute left-1.5 top-1.5 flex flex-col gap-0.5">
              {isNew && (
                <span
                  data-testid="product-badge-new"
                  className="bg-[#00FFC2] px-1.5 py-[1px] font-label text-[7px] font-black text-black tracking-wider leading-tight"
                >
                  NEW
                </span>
              )}
              {isFeatured && (
                <span
                  data-testid="product-badge-featured"
                  className="bg-[#FF5A1F] px-1.5 py-[1px] font-label text-[7px] font-black text-black tracking-wider leading-tight"
                >
                  FEATURED
                </span>
              )}
              {endOfSale ? (
                <span
                  data-testid="product-badge-end-sale"
                  className="bg-red-500 px-1.5 py-[1px] font-label text-[7px] font-black text-white tracking-wider leading-tight animate-pulse"
                >
                  END OF SALE
                </span>
              ) : (
                onSale && (
                  <span
                    data-testid="product-badge-sale"
                    className="bg-[#FFC900] px-1.5 py-[1px] font-label text-[7px] font-black text-black tracking-wider leading-tight"
                  >
                    −{salePct}%
                  </span>
                )
              )}
            </div>

            {/* Top-right category chip */}
            <div className="absolute right-1.5 top-1.5 border border-white/15 bg-black/60 backdrop-blur px-1.5 py-[1px] font-label text-[7px] text-white/70 leading-tight">
              {product.category === "Nicotine Pouches" ? "POUCH" : "VAPE"}
            </div>

            {/* Low-stock nudge */}
            {lowStock && !endOfSale && (
              <span className="absolute left-1.5 bottom-1.5 border border-[#FF5A1F] bg-black/70 backdrop-blur px-1.5 py-[1px] font-label text-[7px] text-[#FF5A1F] leading-tight">
                ONLY {product.stock} LEFT
              </span>
            )}
          </div>

          <div className="p-3 sm:p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-label text-[9px] text-white/40 truncate">{product.brand}</div>
              {rating && rating.count > 0 && (
                <StarRating value={rating.avg} count={rating.count} size={10} />
              )}
            </div>
            <div className="mt-1 font-display text-sm font-bold text-white line-clamp-1">
              {product.name}
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span
                data-testid={PRODUCT.price}
                className="font-display text-base font-black text-[#00FFC2]"
              >
                {ZAR(product.price)}
              </span>
              {onSale && (
                <span className="text-[10px] text-white/40 line-through">
                  {ZAR(product.compare_at)}
                </span>
              )}
            </div>
          </div>
        </Link>

        {/* ---------- BACK FACE ---------- */}
        <div
          data-testid="product-card-back"
          className="absolute inset-0 flex flex-col justify-between border border-[#FF5A1F]/40 bg-gradient-to-br from-[#120704] via-[#0d0d0d] to-[#0a1512] p-4"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <div>
            <div className="font-label text-[9px] text-[#FF5A1F] tracking-wider">
              / {product.brand}
            </div>
            <div className="mt-1 font-display text-sm sm:text-base font-black tracking-tighter line-clamp-2 leading-tight">
              {product.name}
            </div>

            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-base font-black text-[#00FFC2]">
                {ZAR(product.price)}
              </span>
              {onSale && (
                <span className="text-[10px] text-white/40 line-through">
                  {ZAR(product.compare_at)}
                </span>
              )}
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-white/70 line-clamp-4">
              {product.description || "Premium hand-picked flavour, verified authentic and ready to ship."}
            </p>

            {rating && rating.count > 0 && (
              <div className="mt-3">
                <StarRating value={rating.avg} count={rating.count} size={10} />
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-col gap-1.5">
            {onQuickView && (
              <button
                type="button"
                data-testid="product-quickview-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onQuickView(product.slug);
                }}
                className="inline-flex items-center justify-center gap-1.5 bg-[#FF5A1F] py-1.5 font-label text-[9px] text-black hover:-translate-y-[1px] transition-transform"
              >
                <Eye size={10} weight="bold" /> QUICK VIEW
              </button>
            )}
            <div className="flex gap-1.5">
              <Link
                to={`/product/${product.slug}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 border border-white/20 py-1.5 font-label text-[9px] text-white/80 hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
              >
                VIEW DETAILS <ArrowRight size={9} weight="bold" />
              </Link>
              <button
                type="button"
                data-testid={`product-share-btn-${product.slug}`}
                aria-label="Share product"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const url = `https://vapeessence.co.za/product/${product.slug}`;
                  const shareText = `${product.name} · from R${product.price?.toFixed(0)} on Vape Essence`;
                  // Try to include the image via Web Share Level 2
                  let file = null;
                  try {
                    if (product.images?.[0]) {
                      const res = await fetch(mediaUrl(product.images[0]), { mode: "cors" });
                      if (res.ok) {
                        const blob = await res.blob();
                        const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
                        file = new File(
                          [blob],
                          `${product.slug}.${ext}`,
                          { type: blob.type }
                        );
                      }
                    }
                  } catch {
                    /* ignore */
                  }
                  const shareData = { title: product.name, text: shareText, url };
                  if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
                    shareData.files = [file];
                  }
                  if (typeof navigator !== "undefined" && navigator.share) {
                    navigator.share(shareData).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(url);
                    toast.success("Link copied");
                  }
                }}
                className="inline-flex items-center justify-center border border-white/20 px-2 py-1.5 hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
              >
                <ShareNetwork size={10} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
